/**
 * Get ImageData from an image or canvas without touching the DOM.
 * For HTMLImageElement we draw to an offscreen canvas (never appended); for HTMLCanvasElement we use getImageData directly.
 * @param {HTMLImageElement|HTMLCanvasElement} source
 * @returns {ImageData}
 */
export function getImageDataFromSource(source) {
    const w = source.naturalWidth || source.width || source.videoWidth || 0;
    const h = source.naturalHeight || source.height || source.videoHeight || 0;
    if (w <= 0 || h <= 0) {
        throw new Error('Image has no valid dimensions (ensure it is loaded)');
    }
    const readFrequently = { willReadFrequently: true };
    if (source instanceof HTMLCanvasElement) {
        const ctx = source.getContext('2d', readFrequently);
        if (!ctx) throw new Error('Could not get canvas 2d context');
        return { imageData: ctx.getImageData(0, 0, w, h, readFrequently), width: w, height: h };
    }
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', readFrequently);
    if (!ctx) throw new Error('Could not get canvas 2d context');
    try {
        ctx.drawImage(source, 0, 0, w, h);
        return { imageData: ctx.getImageData(0, 0, w, h) };
    } catch (err) {
        if (err instanceof DOMException && err.name === 'SecurityError') {
            throw new Error('Image is from a source that does not allow pixel access (CORS). Please use an image from this site or enable CORS on the image host.');
        }
        throw err;
    }
}

/**
 * Build an OpenCV Mat from an image/canvas using matFromImageData (no DOM, no imread).
 * OpenCV.js matFromImageData accepts ImageData (RGBA) and returns a Mat.
 * @param {typeof import('@techstark/opencv-js')} cv - OpenCV module (from getOpenCv())
 * @param {HTMLImageElement|HTMLCanvasElement} image
 * @returns {import('@techstark/opencv-js').Mat} OpenCV Mat
 */
export function imageToMat(cv, image) {
    const { imageData } = getImageDataFromSource(image);
    const matRGBA = cv.matFromImageData(imageData);
    const matRGB = new cv.Mat();
    cv.cvtColor(matRGBA, matRGB, cv.COLOR_RGBA2RGB);
    matRGBA.delete();
    return matRGB;
}
